import {Assets, Container, Graphics, Sprite, Texture} from "pixi.js";

import {ImageDrawable} from "./image.pixi";
import {FilesService} from "../../../../services/files/files.service";
import {BlueprintContext} from "../../../world/cyanprints/i-blueprint-context";
import {PixiObject} from "../primitives/pixi-object";
import {Subscription} from "rxjs";
import {selectUserById} from "../../../../state/users/users.selector";
import {User} from "../../../../auth/primitives/user";
import {DRect} from "../../../primitives/drawable-rect";
import {IconButtonDrawable} from "./icon-button.pixi";
import {Surface} from "../../../surface/primitives/surface";
import {IconTextButtonDrawable} from "./icon-text-button.pixi";
import {interfaceActions} from "../../../../state/interface/interface.actions";
import {ReactionProperties} from "../../../../state/entities/component-properties-dto/reactions.properties.dto";
import {ReactionButtonDrawable} from "./reaction-button.pixi";
import {selectAuthIsReady} from "../../../../state/auth/auth.selector";
import {FriendReaction} from "../../../../state/reactions/reactions.model";

export class SocialBarDrawable extends PixiObject {
    __debug = false;

    fileService: FilesService;

    profileImage: ImageDrawable;
    profileImageHolder: Container;
    isMe: boolean;
    subscriptions: Subscription[] = []

    background: Graphics

    faIconTextComment = "\uf075"
    faIconTextHeartOpen = '\uf08a'
    faIconTextShare = '\uf064'
    faIconEllipsis = "\uf141"
    iconButtonComment: IconButtonDrawable
    iconButtonHeart: IconTextButtonDrawable
    iconButtonShare: IconButtonDrawable
    iconButtonOptions: IconButtonDrawable

    reactionIcons: ReactionButtonDrawable[] = []

    blueprintContext: BlueprintContext

    heartButtonSolid = false
    heartCount = "0"

    me: User

    constructor(config: {
        blueprintContext: BlueprintContext;
        isMe: boolean
    }) {
        super({id: "social-bar-drawable"});
        this.blueprintContext = config.blueprintContext;
        this.isMe = config.isMe;
        this.profileImageHolder = new Container();
        this.sprite.addChild(this.profileImageHolder);
        this.fileService = config.blueprintContext.networkServices.fileService
        this.buildProfileImage()
        this.subscriptions.push(config.blueprintContext.stateServices.store.select(
            selectUserById(config.blueprintContext.entity.ownerId)
        ).subscribe(async user => {
            if (!user) return
            await this.updateProfileImage(user.profile_image_file_id)
        }))
        this.subscriptions.push(
            this.blueprintContext.stateServices.store.select(selectAuthIsReady)
                .subscribe(async authState => {
            if (!authState) {return}
            this.me = authState.user
        }))
        this.buildOrUpdateButtons(this.blueprintContext.entity.componentProperties.Reactions)
        this.layout()
        this.setupInteractions()
        this.addChildren()
    }

    updateReactions(reactions: ReactionProperties) {
        this.buildOrUpdateButtons(reactions)
        this.layout()
    }

    updateFriendReactions(reactions: FriendReaction[]) {
        if (this.didMeReact(reactions)) {
            this.heartButtonSolid = true
            if (this.iconButtonHeart) {
                this.iconButtonHeart.updateText(this.heartCount + "", this.heartButtonSolid)
                this.requestRender()
            }
        }
    }

    didMeReact(reactions: FriendReaction[]) {
        for (const reaction of reactions) {
            if (reaction.friendId === this.me.id) return true
        }
        return false
    }

    buildOrUpdateButtons(reactions: ReactionProperties) {
        if (this.reactionIcons.length > 0) {
            for (const reaction of this.reactionIcons) {
                reaction.destroy()
            }
        }
        // for (const reactionKey of Object.keys(reactions.commonReactionsCounters)) {
        //     if (reactionKey === this.faIconTextHeartOpen) {
        //         continue
        //     }
        //     const reactionBtn = new ReactionButtonDrawable({
        //         colour: "#858585",
        //         iconWidth: 25,
        //         iconText: reactionKey ,
        //         width: 100,
        //         text:  this.numberToRadixPostfix(reactions.commonReactionsCounters[reactionKey]),
        //         useSolidIcon: false,
        //         fontSize: 10,
        //         blueprintContext: this.blueprintContext
        //     })
        //     this.reactionIcons.push(reactionBtn)
        //     this.addChild(reactionBtn)
        // }
       if (!this.iconButtonComment) {
           this.iconButtonComment = new IconButtonDrawable({
               colour: "#858585",
               width: 30,
               text: this.faIconTextComment,
               useSolidIcon: true,
               blueprintContext: this.blueprintContext
           })
       }
       if (!this.iconButtonShare) {
           this.iconButtonShare = new IconButtonDrawable({
               colour: "#858585",
               width: 30,
               text: this.faIconTextShare,
               useSolidIcon: true,
               blueprintContext: this.blueprintContext
           })
       }
        if (!this.iconButtonOptions) {
            this.iconButtonOptions = new IconButtonDrawable({
                colour: "#858585",
                width: 30,
                text: this.faIconEllipsis,
                useSolidIcon: true,
                blueprintContext: this.blueprintContext
            })
        }
       if (!this.iconButtonHeart) {
           this.iconButtonHeart = new IconTextButtonDrawable({
               colour: "#858585",
               iconWidth: 30,
               iconText: this.faIconTextHeartOpen,
               width: 100,
               text: "0",
               useSolidIcon: false,
               fontSize: 18,
               blueprintContext: this.blueprintContext
           })
       }
       if (!reactions) { return }
       if (Object.keys(reactions.commonReactionsCounters).includes(this.faIconTextHeartOpen)) {
           this.heartCount = this.numberToRadixPostfix(reactions.commonReactionsCounters[this.faIconTextHeartOpen]) || "0"
           this.iconButtonHeart.updateText(this.heartCount + "", this.heartButtonSolid)
           this.sprite.updateCacheTexture()
       }
    }

    numberToRadixPostfix(num: number) {
        if (num < 1000) return num.toString()
        if (num >= 1000 && num < 1000000) return (num / 1000).toFixed(1) + "K"
        if (num >= 1000000) return (num / 1000000).toFixed(1) + "M"
        return num + ""
    }

    addChildren() {
        this.addChild(this.iconButtonComment)
        this.addChild(this.iconButtonHeart)
        this.addChild(this.iconButtonShare)
        this.addChild(this.iconButtonOptions)
        this.addChild(this.profileImage)
    }

    setupInteractions() {
        this.touchPrimitive.onPressed((pt) => {
            this.sprite.alpha = 0.5;
            this.requestRender()
            return true; // Stop propagation
        });
        this.touchPrimitive.onReleased((pt) => {
            this.sprite.alpha = 1;
            this.requestRender()
            return true
        });
        this.profileImage.touchPrimitive.onPressed((pt) => {
            return true
        })
        this.profileImage.touchPrimitive.onReleased((pt) => {
            this.blueprintContext.stateServices.store.dispatch(interfaceActions.openContextMenu())
            this.blueprintContext.stateServices.router.navigate(
                [{outlets: {'context-menu': "context-profile"}}],
                {
                    queryParams: {
                        userId: this.blueprintContext.entity.ownerId,
                        posX: pt.pos.x-150,
                        posY: pt.pos.y
                    },
                    queryParamsHandling: 'merge' // optional but common
                })
            return true; // Stop propagation
        })
        this.iconButtonHeart.touchPrimitive.onPressed((pt) => {
            this.iconButtonHeart.sprite.alpha = 0.5
            console.log("Heart pressed")
            this.requestRender()
            return true
        })
        this.iconButtonHeart.touchPrimitive.onReleased((pt) => {
            this.iconButtonHeart.sprite.alpha = 1
            this.heartButtonSolid = !this.heartButtonSolid
            this.iconButtonHeart.updateText(this.heartCount + "", this.heartButtonSolid)
            this.requestRender()
            this.blueprintContext.networkServices.reactionsService.react({
                reactionString: this.faIconTextHeartOpen,
                referenceId: this.blueprintContext.entity.id,
                referenceType: 'entity',
                remove: false
            })
            return true
        })
        this.iconButtonShare.touchPrimitive.onPressed((pt) => {
            this.iconButtonShare.sprite.alpha = 0.5
            return true
        })
        this.iconButtonShare.touchPrimitive.onReleased((pt) => {
            this.iconButtonShare.sprite.alpha = 1
            this.blueprintContext.networkServices.shareService.shareEntity(
                this.blueprintContext.entity,
                "post",
                pt.pos
            )
            return true
        })
        this.iconButtonOptions.touchPrimitive.onPressed((pt) => {
            this.blueprintContext.stateServices.store.dispatch(interfaceActions.openContextMenu())
            this.blueprintContext.stateServices.router.navigate(
                [{outlets: {'context-menu': "context-post"}}],
                {
                    queryParams: {
                        entityId: this.blueprintContext.entity.id,
                        posX: pt.pos.x - 150,
                        posY: pt.pos.y
                    },
                    queryParamsHandling: 'merge' // optional but common
                })
            return true
        })
    }

    getEnd(primitive: PixiObject) {
        return primitive.sprite.x + primitive.sprite.width
    }

    layout() {
        this.profileImage.sprite.x = 5;
        this.iconButtonComment.sprite.x = 55 + 15
        this.iconButtonHeart.sprite.x = this.getEnd(this.iconButtonComment) + 15
        let reactionsMovingX = this.getEnd(this.iconButtonHeart) + 15
        for (const reaction of this.reactionIcons) {
            reaction.sprite.x = reactionsMovingX
            reaction.sprite.y = 15
            reactionsMovingX += reaction.sprite.width
        }
        this.iconButtonShare.sprite.x = reactionsMovingX + 15
        this.iconButtonOptions.sprite.x = this.getEnd(this.iconButtonShare) + 15
        this.profileImage.sprite.y = 5;
        this.iconButtonComment.sprite.y = 15
        this.iconButtonHeart.sprite.y = 15
        this.iconButtonShare.sprite.y = 15
        this.iconButtonOptions.sprite.y = 15
        const totalWidth = this.iconButtonOptions.sprite.x + this.iconButtonOptions.sprite.width
        this.buildBackground(totalWidth + 15)
        this.updateTouchBounds()
        this.requestRender()
    }

    buildBackground(width: number) {
        if (this.background) {
            this.background.destroy();
        }
        this.background = new Graphics({});
        const rect = new DRect({
            left: 0,
            top: 0,
            right: width,
            bottom: 60,
            roundness: 100,
            colour: '#363636',
            strokeColor: '#37414B',
            strokeWidth: 7,
        });
        rect.render(this.background);
        this.sprite.addChildAt(this.background, 0);
    }

    buildProfileImage() {
        this.profileImage = new ImageDrawable({
            id: `social-bar-profile-image-` + this.blueprintContext.entity.id,
            fitInSquareSize: 50,
            borderRadius: 100,
            blueprintContext: this.blueprintContext,
        });
        this.profileImage.touchPrimitive.showPointerCursor = true
    }

    async updateProfileImage(fileId: string) {
        await this.profileImage.createOrUpdate({fileId})
    }

    override async onAddedToSurface(surface: Surface) {
        super.onAddedToSurface(surface);
    }
}
